import React from 'react'

import {
  Group,
  MantineColor,
  MantineFontSize,
  Stack,
  Text,
} from '@mantine/core'
import { observer } from 'mobx-react-lite'

import { useMst } from '#state'

import { PaidFeature } from './plans'
import { useCanUpgrade } from './useBillingPlan'

export const CONTACT_OWNER_NOTE =
  'Contact your workspace owner to request an upgrade.'

const listFormat = new Intl.ListFormat('en', { type: 'conjunction' })

const capitalize = (text: string) =>
  text.charAt(0).toUpperCase() + text.slice(1)

const PlanNames = ({ plans }: { plans: string[] }) =>
  plans.length === 0 ? (
    <strong>paid plans</strong>
  ) : (
    <>
      {listFormat
        .formatToParts(plans)
        .map((part, i) =>
          part.type === 'element' ? (
            <strong key={i}>{part.value}</strong>
          ) : (
            part.value
          ),
        )}{' '}
      plans
    </>
  )

// Produces eg. "Revisions are only available to Pro and Studio plans"
// followed by `action` for whoever can upgrade, or who to ask otherwise
export const PaidFeatureNote = observer(function PaidFeatureNote({
  features,
  plans = [],
  action,
  inline = false,
  fz,
  c,
}: {
  features: PaidFeature[]
  plans?: string[]
  action?: React.ReactNode
  inline?: boolean
  fz?: MantineFontSize | number
  c?: MantineColor
}) {
  const { currentOrg } = useMst()
  const canUpgrade = useCanUpgrade(currentOrg)
  const names = capitalize(listFormat.format(features.map((f) => f.name)))
  const verb = features.length > 1 || features[0]?.plural ? 'are' : 'is'

  const text = (
    <Stack gap={4}>
      <Text fz={fz} c={c}>
        {names} {verb} only available to <PlanNames plans={plans} />.
      </Text>
      {!canUpgrade && (
        <Text fz="sm" c={c}>
          {CONTACT_OWNER_NOTE}
        </Text>
      )}
    </Stack>
  )

  return inline ? (
    <Group justify="space-between" wrap="nowrap">
      {text}
      {canUpgrade && action}
    </Group>
  ) : (
    <Stack gap="xs">
      {text}
      {canUpgrade && action}
    </Stack>
  )
})
